import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  query,
  runTransaction,
  serverTimestamp,
  Timestamp,
  where,
  type DocumentData,
} from 'firebase/firestore';
import type {
  UserRole,
  UserStatus,
  VerificationRank,
  VerificationStatus,
  VerificationType,
} from '@kulum/shared';
import { db } from '@/lib/firebase';

export const VERIFICATION_KEYS = [
  'identity',
  'education',
  'income',
  'spermTest',
  'stdTest',
] as const satisfies readonly VerificationType[];

export type VerificationKey = (typeof VERIFICATION_KEYS)[number];
export type ReviewDecision = 'approved' | 'rejected' | 'remanded';
export interface ReviewChecksInput {
  birthDateMatched?: boolean;
  incomeMatched?: boolean;
  educationMatched?: boolean;
  stdTestNegative?: boolean;
}
export interface ReviewRanksInput {
  education?: string;
  income?: string;
  spermTest?: string;
  stdTest?: string;
  educationInternal?: string;
}
type ReviewStatus = 'pending' | 'approved' | 'rejected' | 'remanded';
export type ReviewListStatusFilter = 'pending' | 'completed' | 'all';
type UnknownRecord = Record<string, unknown>;

export interface VerificationDocumentItem {
  url: string;
  fileName: string;
  uploadedAt?: Date;
  type?: string;
}

export interface VerificationItemView {
  status: VerificationStatus;
  rank?: string;
  verifiedAt?: Date;
  expiresAt?: Date;
  rejectionReason?: string;
  documents: VerificationDocumentItem[];
}

export interface PendingReviewSummary {
  userId: string;
  displayName: string;
  email: string;
  role: UserRole | 'unknown';
  userStatus: UserStatus | 'unknown';
  residence?: string;
  age?: number;
  requestedAt?: Date;
  reviewedAt?: Date;
  reviewStatus: ReviewStatus;
  reviewTypes: VerificationKey[];
  documentCount: number;
}

export interface ReviewDetail {
  userId: string;
  displayName: string;
  email: string;
  role: UserRole | 'unknown';
  userStatus: UserStatus | 'unknown';
  iconUrl?: string;
  residence?: string;
  birthDate?: Date;
  age?: number;
  declaredValues: {
    birthDate?: Date;
    educationLevel?: string;
    educationSchoolName?: string;
    annualIncome?: string;
  };
  adminReview: {
    status: ReviewStatus;
    reviewedBy?: string;
    reviewedAt?: Date;
    comment?: string;
    checks?: ReviewChecksInput;
    ranks?: ReviewRanksInput;
  } | null;
  items: Record<VerificationKey, VerificationItemView>;
  updatedAt?: Date;
}

export interface ReviewDecisionInput {
  userId: string;
  reviewerId: string;
  decision: ReviewDecision;
  comment?: string;
  checks?: ReviewChecksInput;
  ranks?: ReviewRanksInput;
}

const USER_STATUSES: UserStatus[] = [
  'pending',
  'email_verified',
  'under_review',
  'approved',
  'rejected',
  'suspended',
  'withdrawn',
];

const VERIFICATION_STATUSES: VerificationStatus[] = ['none', 'pending', 'approved', 'rejected'];

const isRecord = (value: unknown): value is UnknownRecord =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

const asOptionalString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const asDate = (value: unknown): Date | undefined => {
  if (value instanceof Timestamp) {
    return value.toDate();
  }

  if (value instanceof Date) {
    return value;
  }

  return undefined;
};

const asUserRole = (value: unknown): UserRole | 'unknown' => {
  if (value === 'donor' || value === 'donee') {
    return value;
  }

  return 'unknown';
};

const asUserStatus = (value: unknown): UserStatus | 'unknown' =>
  USER_STATUSES.includes(value as UserStatus) ? (value as UserStatus) : 'unknown';

const asVerificationStatus = (value: unknown): VerificationStatus =>
  VERIFICATION_STATUSES.includes(value as VerificationStatus) ? (value as VerificationStatus) : 'none';

const asReviewStatus = (value: unknown): ReviewStatus | null => {
  if (value === 'pending' || value === 'approved' || value === 'rejected' || value === 'remanded') {
    return value;
  }

  return null;
};

const asVerificationRank = (value: unknown): VerificationRank | undefined => {
  if (value === 'A' || value === 'B' || value === 'C' || value === 'D') {
    return value;
  }
  return undefined;
};

const asBadgeRank = (value: unknown): VerificationRank | undefined => {
  if (value === 'S' || value === 'A' || value === 'B' || value === 'C' || value === 'D') {
    return value;
  }
  return undefined;
};

const calculateAge = (birthDate?: Date): number | undefined => {
  if (!birthDate) {
    return undefined;
  }

  const now = new Date();
  let age = now.getFullYear() - birthDate.getFullYear();
  const monthDiff = now.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < birthDate.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : undefined;
};

const parseDocuments = (value: unknown): VerificationDocumentItem[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.reduce<VerificationDocumentItem[]>((documents, entry) => {
    if (!isRecord(entry)) {
      return documents;
    }

    const url = asString(entry.url);
    if (!url) {
      return documents;
    }

    documents.push({
      url,
      fileName: asString(entry.fileName) || '提出書類',
      uploadedAt: asDate(entry.uploadedAt),
      type: asOptionalString(entry.type),
    });
    return documents;
  }, []);
};

const parseVerificationItem = (value: unknown): VerificationItemView => {
  if (!isRecord(value)) {
    return {
      status: 'none',
      documents: [],
    };
  }

  return {
    status: asVerificationStatus(value.status),
    rank: asOptionalString(value.rank),
    verifiedAt: asDate(value.verifiedAt),
    expiresAt: asDate(value.expiresAt),
    rejectionReason: asOptionalString(value.rejectionReason),
    documents: parseDocuments(value.documents),
  };
};

const parseVerificationItems = (
  data: UnknownRecord
): Record<VerificationKey, VerificationItemView> => {
  return VERIFICATION_KEYS.reduce<Record<VerificationKey, VerificationItemView>>((items, key) => {
    items[key] = parseVerificationItem(data[key]);
    return items;
  }, {} as Record<VerificationKey, VerificationItemView>);
};

const getBasicInfoRecord = (profileData: DocumentData | undefined): UnknownRecord | null => {
  if (!profileData || !isRecord(profileData.basicInfo)) {
    return null;
  }

  return profileData.basicInfo;
};

const REVIEW_LIST_STATUS_VALUES: Record<ReviewListStatusFilter, ReviewStatus[]> = {
  pending: ['pending'],
  completed: ['approved', 'rejected', 'remanded'],
  all: ['pending', 'approved', 'rejected', 'remanded'],
};

const buildReviewListQuery = (statusFilter: ReviewListStatusFilter) => {
  const statuses = REVIEW_LIST_STATUS_VALUES[statusFilter];
  const verificationsRef = collection(db, 'verifications');

  return statuses.length === 1
    ? query(verificationsRef, where('adminReview.status', '==', statuses[0]))
    : query(verificationsRef, where('adminReview.status', 'in', statuses));
};

export const fetchReviewSummaries = async (
  statusFilter: ReviewListStatusFilter = 'pending'
): Promise<PendingReviewSummary[]> => {
  const reviewsQuery = buildReviewListQuery(statusFilter);

  const verificationSnapshot = await getDocs(reviewsQuery);

  const reviews = await Promise.all(
    verificationSnapshot.docs.map(async (verificationDoc) => {
      const userId = verificationDoc.id;
      const verificationData = verificationDoc.data() as UnknownRecord;
      const items = parseVerificationItems(verificationData);
      const adminReviewRecord = isRecord(verificationData.adminReview)
        ? verificationData.adminReview
        : null;
      const reviewStatus = asReviewStatus(adminReviewRecord?.status) ?? 'pending';

      const [userSnap, profileSnap] = await Promise.all([
        getDoc(doc(db, 'users', userId)),
        getDoc(doc(db, 'profiles', userId)),
      ]);

      const userData = userSnap.data();
      const profileData = profileSnap.data();
      const basicInfo = getBasicInfoRecord(profileData);
      const birthDate = asDate(basicInfo?.birthDate);

      const pendingTypes = VERIFICATION_KEYS.filter((key) => items[key].status === 'pending');
      const submittedTypes = VERIFICATION_KEYS.filter(
        (key) => items[key].status !== 'none' || items[key].documents.length > 0
      );
      const reviewTypes = pendingTypes.length > 0 ? pendingTypes : submittedTypes;
      const documentCount = reviewTypes.reduce(
        (total, key) => total + items[key].documents.length,
        0
      );

      return {
        userId,
        displayName: asString(userData?.displayName) || '名前未設定',
        email: asString(userData?.email) || 'メール未設定',
        role: asUserRole(userData?.role),
        userStatus: asUserStatus(userData?.status),
        residence: asOptionalString(basicInfo?.residence),
        age: calculateAge(birthDate),
        requestedAt: asDate(verificationData.updatedAt),
        reviewedAt: asDate(adminReviewRecord?.reviewedAt),
        reviewStatus,
        reviewTypes,
        documentCount,
      } satisfies PendingReviewSummary;
    })
  );

  return reviews.sort((a, b) => {
    const aTime = a.reviewedAt?.getTime() ?? a.requestedAt?.getTime() ?? 0;
    const bTime = b.reviewedAt?.getTime() ?? b.requestedAt?.getTime() ?? 0;
    return bTime - aTime;
  });
};

export const fetchPendingReviews = async (): Promise<PendingReviewSummary[]> =>
  fetchReviewSummaries('pending');

export const fetchPendingReviewCount = async (): Promise<number> => {
  const snapshot = await getCountFromServer(buildReviewListQuery('pending'));
  return snapshot.data().count;
};

export const fetchReviewDetail = async (userId: string): Promise<ReviewDetail> => {
  const [verificationSnap, userSnap, profileSnap] = await Promise.all([
    getDoc(doc(db, 'verifications', userId)),
    getDoc(doc(db, 'users', userId)),
    getDoc(doc(db, 'profiles', userId)),
  ]);

  if (!verificationSnap.exists()) {
    throw new Error('審査情報が見つかりません');
  }

  const verificationData = verificationSnap.data() as UnknownRecord;
  const userData = userSnap.data();
  const profileData = profileSnap.data();
  const basicInfo = getBasicInfoRecord(profileData);
  const birthDate = asDate(basicInfo?.birthDate);
  const education = isRecord(basicInfo?.education) ? basicInfo.education : null;
  const adminReviewRecord = isRecord(verificationData.adminReview) ? verificationData.adminReview : null;
  const reviewStatus = asReviewStatus(adminReviewRecord?.status);
  const checksRecord = isRecord(adminReviewRecord?.checks) ? adminReviewRecord?.checks : null;
  const ranksRecord = isRecord(adminReviewRecord?.ranks) ? adminReviewRecord?.ranks : null;

  return {
    userId,
    displayName: asString(userData?.displayName) || '名前未設定',
    email: asString(userData?.email) || 'メール未設定',
    role: asUserRole(userData?.role),
    userStatus: asUserStatus(userData?.status),
    iconUrl: asOptionalString(userData?.iconUrl),
    residence: asOptionalString(basicInfo?.residence),
    birthDate,
    age: calculateAge(birthDate),
    declaredValues: {
      birthDate,
      educationLevel: asOptionalString(education?.level),
      educationSchoolName: asOptionalString(education?.schoolName),
      annualIncome: asOptionalString(basicInfo?.annualIncome),
    },
    adminReview: reviewStatus
      ? {
          status: reviewStatus,
          reviewedBy: asOptionalString(adminReviewRecord?.reviewedBy),
          reviewedAt: asDate(adminReviewRecord?.reviewedAt),
          comment: asOptionalString(adminReviewRecord?.comment),
          checks: checksRecord
            ? {
                birthDateMatched:
                  typeof checksRecord.birthDateMatched === 'boolean'
                    ? checksRecord.birthDateMatched
                    : undefined,
                incomeMatched:
                  typeof checksRecord.incomeMatched === 'boolean'
                    ? checksRecord.incomeMatched
                    : undefined,
                educationMatched:
                  typeof checksRecord.educationMatched === 'boolean'
                    ? checksRecord.educationMatched
                    : undefined,
                stdTestNegative:
                  typeof checksRecord.stdTestNegative === 'boolean'
                    ? checksRecord.stdTestNegative
                    : undefined,
              }
            : undefined,
          ranks: ranksRecord
            ? {
                education:
                  typeof ranksRecord.education === 'string'
                    ? ranksRecord.education
                    : typeof ranksRecord.educationInternal === 'string'
                    ? ranksRecord.educationInternal
                    : undefined,
                income:
                  typeof ranksRecord.income === 'string'
                    ? ranksRecord.income
                    : undefined,
                spermTest:
                  typeof ranksRecord.spermTest === 'string'
                    ? ranksRecord.spermTest
                    : undefined,
                stdTest:
                  typeof ranksRecord.stdTest === 'string'
                    ? ranksRecord.stdTest
                    : undefined,
              }
            : undefined,
        }
      : null,
    items: parseVerificationItems(verificationData),
    updatedAt: asDate(verificationData.updatedAt),
  };
};

export const submitReviewDecision = async ({
  userId,
  reviewerId,
  decision,
  comment,
  checks,
  ranks,
}: ReviewDecisionInput): Promise<void> => {
  const normalizedComment = comment?.trim() ?? '';
  const rejectionReason =
    normalizedComment ||
    (decision === 'remanded'
      ? '提出書類との照合に不備があるため差し戻しとなりました。修正後に再申請してください。'
      : '審査基準を満たしていないため却下しました。');
  const normalizedChecks: ReviewChecksInput = {
    birthDateMatched:
      typeof checks?.birthDateMatched === 'boolean' ? checks.birthDateMatched : undefined,
    incomeMatched:
      typeof checks?.incomeMatched === 'boolean' ? checks.incomeMatched : undefined,
    educationMatched:
      typeof checks?.educationMatched === 'boolean' ? checks.educationMatched : undefined,
    stdTestNegative:
      typeof checks?.stdTestNegative === 'boolean' ? checks.stdTestNegative : undefined,
  };
  const educationRank = asVerificationRank(ranks?.education ?? ranks?.educationInternal);
  const incomeRank = asVerificationRank(ranks?.income);
  const spermTestRank = asVerificationRank(ranks?.spermTest);
  const stdTestRank = asVerificationRank(ranks?.stdTest);

  await runTransaction(db, async (transaction) => {
    const verificationRef = doc(db, 'verifications', userId);
    const userRef = doc(db, 'users', userId);
    const profileRef = doc(db, 'profiles', userId);

    const [verificationSnap, userSnap] = await Promise.all([
      transaction.get(verificationRef),
      transaction.get(userRef),
    ]);

    if (!verificationSnap.exists()) {
      throw new Error('審査情報が見つかりません');
    }
    if (!userSnap.exists()) {
      throw new Error('対象ユーザーが見つかりません');
    }

    const verificationData = verificationSnap.data() as UnknownRecord;
    const userData = userSnap.data() as UnknownRecord;
    const role = asUserRole(userData.role);
    if (role === 'unknown') {
      throw new Error('ユーザー種別が不正です');
    }

    const adminReviewData = isRecord(verificationData.adminReview) ? verificationData.adminReview : null;
    if (asReviewStatus(adminReviewData?.status) !== 'pending') {
      throw new Error('この申請は既に処理済みです');
    }

    if (decision === 'approved') {
      if (normalizedChecks.birthDateMatched !== true) {
        throw new Error('承認には「生年月日の照合OK」が必要です');
      }
      if (role === 'donor') {
        if (normalizedChecks.educationMatched !== true) {
          throw new Error('承認には「学歴の照合OK」が必要です');
        }
        if (normalizedChecks.incomeMatched !== true) {
          throw new Error('承認には「年収の照合OK」が必要です');
        }
        if (normalizedChecks.stdTestNegative !== true) {
          throw new Error('承認には「性病検査の陰性確認」が必要です');
        }
        if (!educationRank) {
          throw new Error('承認には学歴ランクの入力が必要です');
        }
        if (!incomeRank) {
          throw new Error('承認には年収ランクの入力が必要です');
        }
        if (!spermTestRank) {
          throw new Error('承認には精液検査ランクの入力が必要です');
        }
        if (!stdTestRank) {
          throw new Error('承認には性病検査ランクの入力が必要です');
        }
      }
    }

    const rankByKey: Partial<Record<VerificationKey, VerificationRank>> = {
      education: educationRank,
      income: incomeRank,
      spermTest: spermTestRank,
      stdTest: stdTestRank,
    };
    const verificationUpdates: UnknownRecord = {};
    for (const key of VERIFICATION_KEYS) {
      const currentItem = isRecord(verificationData[key]) ? ({ ...verificationData[key] } as UnknownRecord) : {};
      const currentStatus = asVerificationStatus(currentItem.status);

      if (!Array.isArray(currentItem.documents)) {
        currentItem.documents = [];
      }

      if (currentStatus === 'pending') {
        if (decision === 'approved') {
          currentItem.status = 'approved';
          currentItem.verifiedAt = serverTimestamp();
          delete currentItem.rejectionReason;
          if (rankByKey[key]) {
            currentItem.rank = rankByKey[key];
          }
        } else if (decision === 'remanded') {
          currentItem.status = 'none';
          delete currentItem.verifiedAt;
          delete currentItem.rejectionReason;
          delete currentItem.rank;
        } else {
          currentItem.status = 'rejected';
          currentItem.rejectionReason = rejectionReason;
          delete currentItem.verifiedAt;
          delete currentItem.rank;
        }
      }

      verificationUpdates[key] = currentItem;
    }

    const adminReviewUpdate: UnknownRecord = {
      status: decision,
      reviewedBy: reviewerId,
      reviewedAt: serverTimestamp(),
      checks: normalizedChecks,
    };
    const adminRanks: UnknownRecord = {};
    if (role === 'donor' && educationRank) {
      adminRanks.education = educationRank;
    }
    if (role === 'donor' && incomeRank) {
      adminRanks.income = incomeRank;
    }
    if (spermTestRank) {
      adminRanks.spermTest = spermTestRank;
    }
    if (role === 'donor' && stdTestRank) {
      adminRanks.stdTest = stdTestRank;
    }
    if (Object.keys(adminRanks).length > 0) {
      adminReviewUpdate.ranks = adminRanks;
    }

    if (normalizedComment.length > 0) {
      adminReviewUpdate.comment = normalizedComment;
    }

    transaction.update(verificationRef, {
      ...verificationUpdates,
      adminReview: adminReviewUpdate,
      updatedAt: serverTimestamp(),
    });

    const resolveStatus = (key: VerificationKey): VerificationStatus => {
      const item = verificationUpdates[key];
      if (!isRecord(item)) {
        return 'none';
      }
      return asVerificationStatus(item.status);
    };
    const resolveRank = (key: VerificationKey): VerificationRank | undefined => {
      const item = verificationUpdates[key];
      if (!isRecord(item)) {
        return undefined;
      }
      return asBadgeRank(item.rank);
    };
    const badgeRanks: Partial<Record<'education' | 'income' | 'spermTest' | 'stdTest', VerificationRank>> = {};
    const badgeEducationRank = resolveStatus('education') === 'approved' ? resolveRank('education') : undefined;
    const badgeIncomeRank = resolveStatus('income') === 'approved' ? resolveRank('income') : undefined;
    const spermRank = resolveStatus('spermTest') === 'approved' ? resolveRank('spermTest') : undefined;
    const stdRank = resolveStatus('stdTest') === 'approved' ? resolveRank('stdTest') : undefined;

    if (badgeEducationRank) {
      badgeRanks.education = badgeEducationRank;
    }
    if (badgeIncomeRank) {
      badgeRanks.income = badgeIncomeRank;
    }
    if (spermRank) {
      badgeRanks.spermTest = spermRank;
    }
    if (stdRank) {
      badgeRanks.stdTest = stdRank;
    }

    transaction.set(
      profileRef,
      {
        verificationBadges: {
          identity: resolveStatus('identity') === 'approved',
          education: resolveStatus('education') === 'approved',
          income: resolveStatus('income') === 'approved',
          spermTest: resolveStatus('spermTest') === 'approved',
          stdTest: resolveStatus('stdTest') === 'approved',
          ranks: badgeRanks,
        },
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );

    const userUpdates: UnknownRecord = {
      updatedAt: serverTimestamp(),
    };

    if (role === 'donor') {
      if (decision === 'approved') {
        userUpdates.status = 'approved';
        userUpdates.identityVerified = true;
        userUpdates.identityVerificationStatus = 'verified';
        userUpdates.rejectedUntil = null;
      } else if (decision === 'remanded') {
        userUpdates.status = 'email_verified';
        userUpdates.identityVerified = false;
        userUpdates.identityVerificationStatus = 'rejected';
        userUpdates.rejectedUntil = null;
      } else {
        const rejectedUntil = new Date();
        rejectedUntil.setMonth(rejectedUntil.getMonth() + 1);
        userUpdates.status = 'rejected';
        userUpdates.identityVerified = false;
        userUpdates.identityVerificationStatus = 'rejected';
        userUpdates.rejectedUntil = Timestamp.fromDate(rejectedUntil);
      }
    } else {
      userUpdates.status = 'email_verified';
      userUpdates.identityVerified = decision === 'approved';
      userUpdates.identityVerificationStatus = decision === 'approved' ? 'verified' : 'rejected';
      userUpdates.rejectedUntil = null;
    }

    transaction.update(userRef, userUpdates);
  });
};
